import React, { useCallback, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import clsx from 'clsx';

import TagManager from '../../../classes/TagManager';
import showLoginErrorMessage from '../../../utils/showLoginErrorMessage';
import { FORM_TYPE } from '../../molecules/account/accountDropdown/AccountDropdown';
import { Link } from '../../atoms/link/Link';
import DiscountTag from '../../atoms/discountTag';
import PromotinalTag from '../../atoms/promotinalTag';
import Button from '@srcDS/components/atoms/button';
import Portal from '@srcDS/components/atoms/portal';
import Price from '../../molecules/price/Price';
import { QuickAdd } from '../../molecules/quickAdd/QuickAdd';
import AddToCartPopup from '@srcDS/components/molecules/addToCartPopup';
import { Text } from '../../typography/text/Text';
import ProductTileGallery from './ProductTileGallery';
import LinkToWishlist from '@srcDS/components/molecules/linkToWishlist/LinkToWishlist';
import { FinalSaleBadge } from '@srcDS/components/atoms/finalSale/FinalSaleBadge';

import { isEmptyArray, isNil } from '@srcDS/utils/assertions';
import { formatUrl, sortSiblings } from '@srcDS/utils/helpers';
import { useOpenSignForm } from '@srcDS/utils/useOpenSignForm';
import { useQuickAdd } from '@srcDS/js/components/molecules/quickAdd/useQuickAdd';

import styles from './ProductTile.module.scss';
import { Item, ProductTileProps, RootState, Sibling } from './ProductTileTypes';

const ProductTile: React.FC<ProductTileProps> = (props) => {
  const {
    promoContent = {},
    images = null,
    productName = '',
    tags = [],
    regularPrice = '',
    discountedPrice = null,
    onSale = false,
    url = '',
    remoteImage,
    inOnStock,
    gridType = 'wide',
    variants = [],
    hideArrows = false,
    name = '',
    categoryTitle,
    productId,
    parentProduct,
    position,
    item,
    placement,
    enableTouchOnMobile,
    productType,
    isTouch,
    listName,
    theme = 'Saba',
    onClick,
    nostoCallback,
    nostoPlacement,
    setProductHeight,
    enableGallery = true,
    isSearchPage,
    preloadImage = false,
    finalSale,
    type = '',
    pathParam,
    nostoRecordSearchClick = () => {},
    ...rest
  } = props;

  const isJagStore = theme === 'Jag';
  const [loading, setLoading] = useState<boolean>(false);
  const [isShowAtcPopup, setIsShowAtcPopup] = useState<boolean>(false);

  const isMobile = useMedia('(max-width: 767px)', false);

  const tagsRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);

  const { signFormOpen } = useOpenSignForm();

  const { currency, isSSR } = useSelector(
    (state: RootState) => ({
      currency: state.app.context.currency,
      isSSR: state.renderContext.serverSideRendering,
    }),
    shallowEqual,
  );

  const triggerNostoClick = useCallback(() => {
    if (pathParam) {
      window.history.pushState('', '', pathParam);
    }
    if (nostoCallback && productId) {
      nostoCallback(productId, nostoPlacement);
    }
  }, [nostoCallback, productId, nostoPlacement, pathParam]);

  const label = onSale ? 'Sale' : item?.attributes?.newarrival ? 'New' : '';
  const newLabel = item?.attributes?.newarrival ? 'New' : '';

  const nostoSearchClickData = useMemo(() => {
    const hit = {
      productId,
      url: formatUrl(url),
    };
    return {
      type,
      hit,
    };
  }, [productId, url, type]);

  const hasSize = variants?.some((x) => x.attributes?.size);

  const fillDefaultElement = (el?: Item): Sibling => ({
    colour: el?.attributes?.colour || '',
    id: el?.id,
    swatch:
      (el?.attributes?.swatch_image && el?.attributes?.swatch_image) ||
      el?.images?.find((img) => img.label === 'swatch')?.url ||
      parentProduct?.images?.find((img) => img.label === 'swatch')?.url ||
      el?.images?.[0]?.url ||
      el?.image_url ||
      el?.images?.[0] || // Algolia
      el?.imageUrl ||
      '',
    url: parentProduct?._url || parentProduct?.url || '',
  });

  const swatches = useMemo(() => {
    const currentElement = fillDefaultElement(item);
    const siblings = isSearchPage ? item?.attributes?.siblings : parentProduct?.projectSpecificData?.siblings;
    const _siblings = Array.isArray(siblings) ? siblings : siblings instanceof Object ? Object.values(siblings) : [];
    const formattedSiblingsArray =
      typeof _siblings === 'object' && !isEmptyArray(_siblings) ? [currentElement, ..._siblings] : [currentElement];
    const sortedSiblings = sortSiblings(formattedSiblingsArray);
    const items = sortedSiblings?.map((sibling, idx) => ({
      imageUrl: sibling?.swatch,
      id: `sibling-${parentProduct?.productId}-${idx}`,
      url: sibling?.url || url,
      colorAttribute: sibling?.colour,
    }));
    return items ? items : [fillDefaultElement(item)];
  }, [item, variants, url, isSearchPage, parentProduct]);

  const tagsElement = useMemo(() => {
    const hasTags = Array.isArray(tags) && tags.some(Boolean);

    if (!hasTags && !newLabel) return null;

    return (
      <div className={styles.tagHolder}>
        <div ref={tagsRef} className={styles.tags}>
          {(isJagStore && newLabel) && (
            <Text className={clsx(styles.tag)} size="xs" as="span" weight="light">
              {newLabel}
            </Text>
          )}
          {hasTags &&
            tags.map(
              (it, index) =>
                !!it && (
                  <Text className={styles.tag} key={index} size="xs" as="span" weight="light">
                    {it}
                  </Text>
                ),
            )}
        </div>
      </div>
    );
  }, [tags, newLabel, isJagStore]);

  const renderJagSaleBlock = useCallback((
    finalSale: boolean,
    onSale?: boolean,
    label?: string,
    barrierPosition: 'none' | 'left' = 'none',
    isJagStore: boolean = true,
  ): React.ReactNode => {
    if (!finalSale && !onSale && !label) return null;

    const badge = (
      <FinalSaleBadge
        className={{
          root: styles.finalSale_root,
          message: styles.finalSale_message,
          barrier: styles.finalSale_barrier,
        }}
        barrierPosition={barrierPosition}
      />
    );

    if (isJagStore) {
      return (
        <>
          {finalSale && badge}
          {!finalSale && onSale && (
            <>
              {!isMobile && <span className={styles.finalSale_barrier} />}
              <Text className={clsx(styles.label, styles.labelOnSale)} as="span">
                Sale
              </Text>
            </>
          )}
        </>
      );
    }

    return (
      <>
        {finalSale ? (
          <FinalSaleBadge
            className={{
              root: styles.finalSale_root,
              message: styles.finalSale_message,
              barrier: styles.finalSale_barrier,
            }}
            barrierPosition="none"
          />
        ) : (
          label && (
            <Text
              className={clsx(styles.label, {
                [styles.labelOnSale]: label.toLowerCase() === 'sale',
              })}
              as="span"
            >
              {label}
            </Text>
          )
        )}
      </>
    );
  }, [isMobile]);

  const getPriceContent = useCallback(() => {
    const hasDiscount = !!discountedPrice && discountedPrice !== regularPrice;
    return (
      <div
        id="test"
        className={clsx(styles.price, {
          [styles.hasDiscount]: true,
          [styles.priceDesktop]: !isMobile,
        })}
      >
        <div className={styles.priceWrap}>
          {regularPrice && regularPrice !== '0' ? (
            <Price
              type="regular"
              placement="regular_product_slider"
              weight={'regular'}
              customClasses={{
                price_font_regular: styles.price_font_regular,
                price_regular_product_slider: styles.price_regular_product_slider,
                priceOnSale: styles.priceOnSale,
              }}
              onSale={hasDiscount}
              value={Number(regularPrice)}
            />
          ) : null}
          {hasDiscount ? (
            <Price
              weight={'regular'}
              type={onSale ? 'sale' : 'regular'}
              placement="regular_product_slider"
              customClasses={{
                price_font_regular: styles.price_font_regular,
                price_regular_product_slider: styles.price_regular_product_slider,
                priceOnSale: styles.priceOnSale,
              }}
              value={Number(discountedPrice)}
            />
          ) : null}
          {!isMobile && (finalSale || onSale) && isJagStore &&
            renderJagSaleBlock(finalSale, onSale, label, 'left', true)}
        </div>
        {isMobile && (finalSale || onSale) && isJagStore &&
          renderJagSaleBlock(finalSale, onSale, label, 'none', true)}
      </div>
    );
  }, [discountedPrice, onSale, regularPrice, finalSale, isMobile]);

  const discountTag = (placement: string) =>
    discountedPrice ? (
      <DiscountTag
        fullPrice={Number(regularPrice)}
        discountPrice={Number(discountedPrice)}
        customStyles={styles.discountTag}
        placement={placement}
      />
    ) : null;

  const isGiftCard = productType === 'gift-card' || productType === 'gift-voucher';

  const firstSkuId = useMemo(() => {
    // Search dropdown (Algolia)
    if (item?.variants && placement === 'Listing Page | Search') {
      return item?.variants?.[0]?.sku;
    }
    // Search page (Nosto)
    if (item?.attributes?.variants) {
      return item?.attributes?.variants?.[0]?.sku;
    }
    // PLP
    return variants?.[0]?.sku;
  }, [item, variants, placement]);

  const { sizeButtons } = useQuickAdd({
    product: {
      price: regularPrice,
      discountedPrice,
      productId,
      name: productName,
      variants: [{ currency }],
    },
    variants,
    nostoPlacement,
    nostoSearchClickData,
    callbackFn: () => setIsShowAtcPopup(false),
  });

  return (
    <div
      className={clsx(styles.root, {
        [styles.narrowTile]: gridType === 'narrow',
        [styles.wideTile]: gridType === 'wide',
        [styles[type]]: type,
      })}
      data-scroll-id={`product-tile-${productId}`}
      ref={tileRef}
      data-stock={inOnStock}
      onClick={() => {
        if (productId && formatUrl(url)) {
          const { type, hit } = nostoSearchClickData;
          nostoRecordSearchClick?.(type, hit);
        }

        if (onClick) {
          onClick(tileRef);
        }
        if (item?.sku && productId) {
          TagManager.productClick(
            {
              name: productName,
              sku: item.sku,
              currencyCode: currency,
              productId,
              price: regularPrice,
              categories: parentProduct?.categories || '',
              categoryTitle: categoryTitle || '',
              position: position || 0,
              parentProduct,
            },
            placement,
            listName,
          );
        }
      }}
      {...rest}
    >
      <div className={styles.galleryHolder}>
        {!isSSR && (
          <ProductTileGallery
            preloadImage={preloadImage}
            nostoCallback={triggerNostoClick}
            enableGallery={enableGallery}
            showSwiper={false}
            remote={remoteImage}
            setProductHeight={setProductHeight}
            images={images}
            url={formatUrl(url)}
            productName={name || productName}
            hideArrows
            enableTouchOnMobile
            productId={`product-tile-${productId}`}
            productOverlayUrl={item?.attributes?.productoverlayurl}
            sourceType={type}
          />
        )}
        {/*@ts-ignore*/}
        {!isGiftCard && <QuickAdd className={clsx(styles.quickAdd)} variants={variants} sizeButtons={sizeButtons} />}
      </div>
      <div
        className={clsx(styles.wrapper, gridType === 'narrow' && isMobile ? styles.wrapperNarrow : styles.wrapperWide)}
      >
        <div className={styles.infoWrap}>
          <div className={styles.infoRow}>
            <div>
              {!isJagStore &&
                renderJagSaleBlock(finalSale, onSale, label, 'none', false)}
              {/* @ts-ignore */}
              <Link ariaLabel={productName} link={formatUrl(url)} onClick={triggerNostoClick}>
                <Text className={styles.productName} as="h2" size="xs" weight="normal">
                  {productName}
                </Text>
              </Link>
            </div>
            <div className={clsx(styles.wishlist, { [styles.containLabel]: label })}>
              <LinkToWishlist
                item={{
                  productName: productName || name,
                  lineItemId: productId,
                  variant: {
                    ...item,
                    price: regularPrice,
                    discountedPrice,
                    sku: firstSkuId,
                  },
                }}
                lineItemId={productId}
                firstSkuId={firstSkuId}
                toSignUp={() => {
                  signFormOpen(FORM_TYPE.LOGIN);
                  showLoginErrorMessage();
                }}
                setLoading={setLoading}
                loading={loading}
                isIcon
              />
            </div>
          </div>
          <div
            className={clsx(styles.infoRow, styles.infoRowBottom, {
              [styles.hasDiscount]: finalSale || discountedPrice,
            })}
          >
            <div>
              {getPriceContent()}
              {!isGiftCard &&
                ((isJagStore && newLabel) || (!isNil(variants) && !isEmptyArray(variants))) &&
                tagsElement}
              {swatches?.length >= 2 && (
                <Text className={styles.moreColours} size="xs" as="p" weight="normal">
                  More colours
                </Text>
              )}
              {promoContent?.name ? (
                <PromotinalTag data={promoContent} gridType={gridType} placement="plp-left" />
              ) : (
                discountTag('plp-left')
              )}
              {promoContent?.name ? (
                <PromotinalTag data={promoContent} gridType={gridType} placement="plp-wide-mobile" />
              ) : (
                discountTag('plp-wide-mobile')
              )}
            </div>
          </div>
        </div>
      </div>
      {!isGiftCard && hasSize && (
        <div data-trigger="quickadd" className={clsx(styles.quickAddTrigger)}>
          {/*@ts-ignore*/}
          <Button variant="text" onClick={() => setIsShowAtcPopup(true)}>
            Add to bag +
          </Button>
        </div>
      )}
      {isShowAtcPopup && (
        // @ts-ignore
        <Portal>
          <AddToCartPopup
            /*@ts-ignore*/
            sizeButtons={sizeButtons}
            product={{
              title: productName,
              subtitle: item?.attributes?.colour?.toLowerCase() || '',
            }}
            onClose={() => setIsShowAtcPopup(false)}
          />
        </Portal>
      )}
    </div>
  );
};

export default ProductTile;
